// Left-most rail. Layout, top → bottom:
//
//   1. Home (Friends + DMs hub) — the strawberry cloud bubble.
//      Equivalent to Discord's home/DM home button. Active state
//      lights the bubble with the strawberry-gradient.
//   2. A short separator pip.
//   3. The user's Personal Workspace — square-ish bubble.
//   4. A second separator.
//   5. Shared Workspaces — square bubbles.
//   6. Communities — fully round bubbles.
//   7. A "+" button to create or join.
//   8. Settings cog at the bottom.
//
// Visual identity for the three Space types:
//
//   - Personal Workspace : rounded-2xl bubble with a small label / icon.
//   - Shared Workspace   : rounded-xl bubble (Discord-style).
//   - Community          : fully round bubble. Selection is communicated
//     by the rail indicator instead of a colored ring around the icon.

import {
  BellOff,
  Cloud,
  Copy,
  LogOut,
  Plus,
  Settings,
  Trash2,
  UserPlus,
} from "lucide-react";
import { useEffect, useRef, useState, type MouseEvent } from "react";

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from "@link/ui";

import type { SpaceView } from "../types/ui";
import { api } from "../lib/api";
import { moveItem } from "../lib/reorder";
import { openAfterOverlayDismiss } from "../lib/overlay-transition";
import { useSelf } from "../state/SelfSession";
import { SpaceSetupDialog } from "./SpaceSetupDialog";
import { ServerInviteDialog } from "./ServerInviteDialog";
import { ServerSettingsModal } from "./ServerSettingsModal";

interface Props {
  homeActive: boolean;
  dmUnreadCount: number;
  spaces: SpaceView[];
  activeSpaceXid: string | null;
  onHomeClick: () => void;
  onSpaceClick: (xid: string) => void;
  onSpacesChanged: () => void;
  onSpaceRemoved: () => void;
  mobile?: boolean;
}

/**
 * Per-space-type style adapter for the rail bubble. Returns the
 * `className` segments to apply on the inactive base, the active
 * highlight, and the rounded shape used for the hover transition.
 */
function spaceStyle(spaceType: SpaceView["spaceType"]): {
  baseShape: string;
  activeShape: string;
  baseBg: string;
  activeBg: string;
} {
  switch (spaceType) {
    case "personal_workspace":
      return {
        baseShape: "rounded-2xl",
        activeShape: "rounded-2xl",
        baseBg:
          "bg-white text-strawberry-600 shadow-sm hover:bg-strawberry-50",
        activeBg:
          "bg-strawberry-gradient text-white shadow-sm",
      };
    case "community":
      return {
        baseShape: "rounded-full",
        activeShape: "rounded-full",
        baseBg:
          "bg-white text-emerald-700 shadow-sm hover:bg-emerald-50",
        activeBg:
          "bg-gradient-to-br from-emerald-400 to-emerald-600 text-white shadow-sm",
      };
    case "shared_workspace":
    default:
      return {
        baseShape: "rounded-3xl",
        activeShape: "rounded-xl",
        baseBg:
          "bg-white text-strawberry-700 shadow-sm hover:rounded-xl hover:bg-strawberry-gradient hover:text-white",
        activeBg:
          "bg-strawberry-gradient text-white shadow-sm",
      };
  }
}

interface BubbleProps {
  label: string;
  tooltip: string;
  active: boolean;
  spaceType: SpaceView["spaceType"];
  iconUrl?: string | null;
  onClick: () => void;
  onContextMenu: (event: MouseEvent<HTMLButtonElement>) => void;
}

function SpaceBubble({ label, tooltip, active, spaceType, iconUrl, onClick, onContextMenu }: BubbleProps) {
  const style = spaceStyle(spaceType);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          onContextMenu={onContextMenu}
          aria-current={active ? "page" : undefined}
          className={cn(
            "liquid-rail-button relative flex h-12 w-12 items-center justify-center text-sm font-semibold transition-all duration-200",
            active
              ? cn(style.activeBg, style.activeShape)
              : cn(style.baseBg, style.baseShape),
          )}
        >
          {iconUrl !== null && iconUrl !== undefined ? (
            <img src={iconUrl} alt="" className="h-full w-full rounded-[inherit] object-cover" />
          ) : (
            label
          )}
          {active && (
            <span className="absolute -left-3 top-1/2 h-6 w-1.5 -translate-y-1/2 rounded-r-full bg-strawberry-500" />
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent side="right">{tooltip}</TooltipContent>
    </Tooltip>
  );
}

export function ServerRail({
  homeActive,
  dmUnreadCount,
  spaces,
  activeSpaceXid,
  onHomeClick,
  onSpaceClick,
  onSpacesChanged,
  onSpaceRemoved,
  mobile = false,
}: Props) {
  const self = useSelf();
  const [setupOpen, setSetupOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState<{
    space: SpaceView;
    x: number;
    y: number;
  } | null>(null);
  const [inviteSpace, setInviteSpace] = useState<SpaceView | null>(null);
  const [settingsSpace, setSettingsSpace] = useState<SpaceView | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busySpaceXid, setBusySpaceXid] = useState<string | null>(null);

  // Local copy so drag-reorder can rewrite without losing the
  // upstream prop on the next render.
  const [order, setOrder] = useState<SpaceView[]>(spaces);
  const orderDirty = useRef(false);
  useEffect(() => {
    orderDirty.current = false;
    setOrder(spaces);
  }, [spaces]);
  useEffect(() => {
    if (!orderDirty.current) return undefined;
    const t = window.setTimeout(() => {
      orderDirty.current = false;
      void api.me
        .setSidebarOrder({ space_xids_in_order: order.map((space) => space.xid) })
        .catch((reason: unknown) => {
          setActionError(
            reason instanceof Error
              ? reason.message
              : "Couldn't save the server order.",
          );
          onSpacesChanged();
        });
    }, 600);
    return () => window.clearTimeout(t);
  }, [onSpacesChanged, order]);

  const [dragXid, setDragXid] = useState<string | null>(null);

  const sharedWorkspaces = order.filter(
    (s) => s.spaceType === "shared_workspace",
  );
  const communities = order.filter((s) => s.spaceType === "community");

  const onReorder = (
    type: SpaceView["spaceType"],
    fromXid: string,
    toXid: string,
  ): void => {
    if (fromXid === toXid) return;
    const list = order.filter((s) => s.spaceType === type);
    const fromIdx = list.findIndex((s) => s.xid === fromXid);
    const toIdx = list.findIndex((s) => s.xid === toXid);
    if (fromIdx === -1 || toIdx === -1) return;
    const reordered = moveItem(list, fromIdx, toIdx);
    const next: SpaceView[] = [];
    for (const space of order) {
      if (space.spaceType === type) {
        const replacement = reordered.shift();
        if (replacement !== undefined) next.push(replacement);
      } else {
        next.push(space);
      }
    }
    orderDirty.current = true;
    setOrder(next);
  };

  const openContextMenu = (
    event: MouseEvent<HTMLButtonElement>,
    space: SpaceView,
  ): void => {
    event.preventDefault();
    setActionError(null);
    setContextMenu({ space, x: event.clientX, y: event.clientY });
  };

  const setMuted = async (space: SpaceView, muted: boolean): Promise<void> => {
    setBusySpaceXid(space.xid);
    setActionError(null);
    try {
      await api.spaces.setNotificationMuted(space.xid, muted);
      setOrder((current) =>
        current.map((item) =>
          item.xid === space.xid ? { ...item, notificationMuted: muted } : item,
        ),
      );
      setContextMenu((current) =>
        current?.space.xid === space.xid
          ? { ...current, space: { ...current.space, notificationMuted: muted } }
          : current,
      );
      onSpacesChanged();
    } catch (reason) {
      setActionError(reason instanceof Error ? reason.message : "Couldn't update notifications.");
    } finally {
      setBusySpaceXid(null);
    }
  };

  const leaveSpace = async (space: SpaceView): Promise<void> => {
    if (!window.confirm(`Leave ${space.name}? You can only return with a new invite.`)) return;
    setBusySpaceXid(space.xid);
    setActionError(null);
    try {
      await api.spaces.removeMember(space.xid, self.identity.accountXid);
      setContextMenu(null);
      onSpaceRemoved();
    } catch (reason) {
      setActionError(reason instanceof Error ? reason.message : "Couldn't leave this server.");
    } finally {
      setBusySpaceXid(null);
    }
  };

  const deleteSpace = async (space: SpaceView): Promise<void> => {
    if (!window.confirm(`Delete ${space.name}? It will disappear immediately and enter the recovery window.`)) return;
    setBusySpaceXid(space.xid);
    setActionError(null);
    try {
      await api.spaces.delete(space.xid);
      setContextMenu(null);
      onSpaceRemoved();
    } catch (reason) {
      setActionError(reason instanceof Error ? reason.message : "Couldn't delete this server.");
    } finally {
      setBusySpaceXid(null);
    }
  };

  return (
    <aside
      className={cn(
        "glass-panel w-[68px] flex-col items-center gap-2 border-r border-cloud-200 bg-white px-2 py-3 md:w-[76px] md:px-3",
        mobile ? "flex" : "hidden md:flex",
      )}
    >
      {/* Home: Friends + DM hub. Distinct cloud icon and a hover→
          square transition matching Discord's home button. */}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onHomeClick}
            aria-label="Friends & Direct Messages"
            aria-current={homeActive ? "page" : undefined}
            className={cn(
              "liquid-rail-button relative flex h-12 w-12 items-center justify-center transition-all duration-200",
              homeActive
                ? "rounded-2xl bg-strawberry-gradient text-white shadow-md shadow-strawberry-300/60 ring-2 ring-strawberry-400"
                : "rounded-3xl bg-white text-strawberry-600 ring-2 ring-strawberry-200 shadow-sm hover:rounded-2xl hover:bg-strawberry-gradient hover:text-white hover:ring-strawberry-400",
            )}
          >
            <Cloud className="h-5 w-5" />
            {dmUnreadCount > 0 ? (
              <span
                className="absolute -right-1.5 -top-1.5 flex min-h-5 min-w-5 items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-black leading-none text-white ring-2 ring-white shadow-sm"
                aria-label={`${dmUnreadCount} unread direct ${dmUnreadCount === 1 ? "message" : "messages"}`}
              >
                {dmUnreadCount > 99 ? "99+" : dmUnreadCount}
              </span>
            ) : null}
            {homeActive && (
              <span className="absolute -left-3 top-1/2 h-6 w-1.5 -translate-y-1/2 rounded-r-full bg-strawberry-500" />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent side="right">Friends &amp; Direct Messages</TooltipContent>
      </Tooltip>

      {(sharedWorkspaces.length > 0 || communities.length > 0) && (
        <div className="my-1 h-[2px] w-8 rounded-full bg-cloud-200" />
      )}

      {/* Shared workspaces */}
      {sharedWorkspaces.map((space) => (
        <div
          key={space.xid}
          draggable
          onDragStart={(e) => {
            setDragXid(space.xid);
            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData("text/plain", space.xid);
          }}
          onDragEnd={() => setDragXid(null)}
          onDragOver={(e) => {
            if (
              dragXid !== null &&
              dragXid !== space.xid &&
              order.find((s) => s.xid === dragXid)?.spaceType ===
                "shared_workspace"
            ) {
              e.preventDefault();
              e.dataTransfer.dropEffect = "move";
            }
          }}
          onDrop={(e) => {
            e.preventDefault();
            if (dragXid !== null) {
              onReorder("shared_workspace", dragXid, space.xid);
              setDragXid(null);
            }
          }}
          className={cn(
            "transition-opacity",
            dragXid === space.xid && "opacity-40",
          )}
        >
          <SpaceBubble
            label={space.iconLabel}
            tooltip={`${space.name}  ·  Shared`}
            active={!homeActive && activeSpaceXid === space.xid}
            spaceType="shared_workspace"
            iconUrl={space.iconUrl}
            onClick={() => onSpaceClick(space.xid)}
            onContextMenu={(event) => openContextMenu(event, space)}
          />
        </div>
      ))}

      {/* Communities */}
      {communities.map((space) => (
        <div
          key={space.xid}
          draggable
          onDragStart={(e) => {
            setDragXid(space.xid);
            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData("text/plain", space.xid);
          }}
          onDragEnd={() => setDragXid(null)}
          onDragOver={(e) => {
            if (
              dragXid !== null &&
              dragXid !== space.xid &&
              order.find((s) => s.xid === dragXid)?.spaceType === "community"
            ) {
              e.preventDefault();
              e.dataTransfer.dropEffect = "move";
            }
          }}
          onDrop={(e) => {
            e.preventDefault();
            if (dragXid !== null) {
              onReorder("community", dragXid, space.xid);
              setDragXid(null);
            }
          }}
          className={cn(
            "transition-opacity",
            dragXid === space.xid && "opacity-40",
          )}
        >
          <SpaceBubble
            label={space.iconLabel}
            tooltip={`${space.name}  ·  Community`}
            active={!homeActive && activeSpaceXid === space.xid}
            spaceType="community"
            iconUrl={space.iconUrl}
            onClick={() => onSpaceClick(space.xid)}
            onContextMenu={(event) => openContextMenu(event, space)}
          />
        </div>
      ))}

      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => setSetupOpen(true)}
            aria-label="Create or join a space"
            className="liquid-rail-button flex h-12 w-12 items-center justify-center rounded-3xl border border-dashed border-cloud-300 bg-white text-cloud-500 transition-all duration-200 hover:rounded-2xl hover:border-strawberry-400 hover:text-strawberry-600"
          >
            <Plus className="h-5 w-5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="right">Create or join a space</TooltipContent>
      </Tooltip>

      <SpaceSetupDialog
        open={setupOpen}
        onOpenChange={setSetupOpen}
        onChanged={onSpacesChanged}
      />

      {inviteSpace !== null ? (
        <ServerInviteDialog
          open
          space={inviteSpace}
          onOpenChange={(open) => { if (!open) setInviteSpace(null); }}
        />
      ) : null}

      {settingsSpace !== null ? (
        <ServerSettingsModal
          open
          space={settingsSpace}
          onOpenChange={(open) => { if (!open) setSettingsSpace(null); }}
          onChanged={onSpacesChanged}
          onDeleted={() => { setSettingsSpace(null); onSpaceRemoved(); }}
        />
      ) : null}

      <DropdownMenu
        open={contextMenu !== null}
        onOpenChange={(open) => { if (!open) setContextMenu(null); }}
      >
        <DropdownMenuTrigger asChild>
          <span
            aria-hidden
            style={{
              position: "fixed",
              top: contextMenu?.y ?? 0,
              left: contextMenu?.x ?? 0,
              width: 1,
              height: 1,
              pointerEvents: "none",
            }}
          />
        </DropdownMenuTrigger>
        {contextMenu !== null ? (
          <DropdownMenuContent sideOffset={4} className="w-64 border-cloud-300 bg-white p-2 shadow-2xl">
            <DropdownMenuLabel className="truncate px-2 normal-case text-cloud-800">
              {contextMenu.space.name}
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-cloud-200" />
            <DropdownMenuItem onSelect={() => setInviteSpace(contextMenu.space)}>
              <UserPlus className="h-4 w-4" /> Invite people
            </DropdownMenuItem>
            {contextMenu.space.ownerAccountXid === self.identity.accountXid ? (
              <DropdownMenuItem
                onSelect={() => {
                  const selectedSpace = contextMenu.space;
                  openAfterOverlayDismiss(
                    () => setContextMenu(null),
                    () => setSettingsSpace(selectedSpace),
                  );
                }}
              >
                <Settings className="h-4 w-4" /> Server settings
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuCheckboxItem
              checked={contextMenu.space.notificationMuted === true}
              disabled={busySpaceXid === contextMenu.space.xid}
              onSelect={(event) => event.preventDefault()}
              onCheckedChange={(checked) => void setMuted(contextMenu.space, checked === true)}
              className="gap-2"
            >
              <BellOff className="h-4 w-4" /> Mute server
            </DropdownMenuCheckboxItem>
            <DropdownMenuItem onSelect={() => void navigator.clipboard.writeText(contextMenu.space.xid)}>
              <Copy className="h-4 w-4" /> Copy server ID
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-cloud-200" />
            {contextMenu.space.ownerAccountXid === self.identity.accountXid ? (
              <DropdownMenuItem
                disabled={busySpaceXid === contextMenu.space.xid}
                onSelect={() => void deleteSpace(contextMenu.space)}
                className="text-red-600 focus:bg-red-50 focus:text-red-700"
              >
                <Trash2 className="h-4 w-4" /> Delete server
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                disabled={busySpaceXid === contextMenu.space.xid}
                onSelect={() => void leaveSpace(contextMenu.space)}
                className="text-red-600 focus:bg-red-50 focus:text-red-700"
              >
                <LogOut className="h-4 w-4" /> Leave server
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        ) : null}
      </DropdownMenu>

      {actionError !== null ? (
        <div className="fixed bottom-4 left-24 z-[110] max-w-sm rounded-xl border border-red-200 bg-white px-4 py-3 text-sm text-red-700 shadow-xl">
          {actionError}
        </div>
      ) : null}

      <div className="mt-auto" />

      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => self.setSettingsOpen(true)}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-cloud-500 ring-1 ring-cloud-200 transition-colors hover:bg-cloud-50 hover:text-strawberry-600"
          >
            <Settings className="h-4 w-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="right">Settings</TooltipContent>
      </Tooltip>
    </aside>
  );
}
